import { useState } from 'react';
import { Box, Button, HStack, Input, Stack, Text, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Plus } from 'lucide-react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { testIds } from './testIds';

export type SelectedEmployer = Pick<
  RouterOutputs['workPlacements']['employers']['listEmployers']['items'][number],
  'id' | 'name' | 'locality' | 'state'
>;

function formatLocation(employer: { locality: string | null; state: string | null }): string {
  return [employer.locality, employer.state]
    .filter((part) => part != null && part !== '')
    .join(', ');
}

export function SelectEmployer({
  selected,
  onSelect,
  onAddNew,
}: {
  selected: SelectedEmployer | null;
  onSelect: (employer: SelectedEmployer) => void;
  onAddNew?: () => void;
}): React.ReactElement {
  const [search, setSearch] = useState('');
  const { data, isLoading, isError, refetch } =
    trpcAdmin.workPlacements.employers.listEmployers.useQuery(
      {
        search,
        includeDoNotUse: false,
        pagination: { page: 1, limit: 20 },
      },
      { placeholderData: (previousData) => previousData },
    );

  return (
    <VStack align="stretch" gap={4} w="full">
      {selected != null ? (
        <HStack gap={2} flexWrap="wrap" role="status" data-testid={testIds.selectedEmployer}>
          <Text fontSize="sm" color="fg.muted">
            <Trans>Selected:</Trans>
          </Text>
          <Text fontWeight="semibold">{selected.name}</Text>
          <Text fontSize="sm" color="fg.muted">
            {formatLocation(selected)}
          </Text>
        </HStack>
      ) : null}
      <HStack gap={2}>
        <Input
          data-testid={testIds.searchInput}
          placeholder={t`Search`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
            }
          }}
        />
        {onAddNew != null ? (
          <Button
            variant="outline"
            leftIcon={<Plus size={iconSize.md} />}
            onClick={onAddNew}
            data-testid={testIds.addEmployerButton}
          >
            <Trans>Add new</Trans>
          </Button>
        ) : null}
      </HStack>

      <Stack gap={0} borderWidth="1px" borderRadius="md" overflow="hidden">
        {isLoading ? (
          <Box p={4}>
            <Text color="fg.muted">
              <Trans>Loading…</Trans>
            </Text>
          </Box>
        ) : isError ? (
          <HStack p={4} justify="space-between" gap={3}>
            <Text color="danger">
              <Trans>Unable to load employers.</Trans>
            </Text>
            <Button
              variant="link"
              size="sm"
              onClick={() => void refetch()}
              data-testid={testIds.retryEmployersButton}
            >
              <Trans>Try again</Trans>
            </Button>
          </HStack>
        ) : (data?.items ?? []).length === 0 ? (
          <Box p={4}>
            <Text color="fg.muted">
              <Trans>No employers found.</Trans>
            </Text>
          </Box>
        ) : (
          (data?.items ?? []).map((employer) => (
            <Box
              key={employer.id}
              as="button"
              type="button"
              textAlign="left"
              px={4}
              py={3}
              borderBottomWidth="1px"
              _last={{ borderBottomWidth: 0 }}
              bg={employer.id === selected?.id ? 'bg.muted' : undefined}
              aria-pressed={employer.id === selected?.id}
              _hover={{ bg: 'bg.muted' }}
              onClick={() => onSelect(employer)}
              data-testid={testIds.employerOption}
            >
              <Text fontWeight="semibold">{employer.name}</Text>
              <Text fontSize="sm" color="fg.muted">
                {formatLocation(employer)}
              </Text>
            </Box>
          ))
        )}
      </Stack>
    </VStack>
  );
}
